declare global {
  interface Window {
    gtag: (command: string, targetId: string, config?: any) => void;
  }
}

export const EVENT_CATEGORIES = {
  USER_ENGAGEMENT: 'user_engagement',
  NAVIGATION: 'navigation',
  FORM_INTERACTION: 'form_interaction',
  CONTENT_INTERACTION: 'content_interaction',
  MEDICAL_GUIDELINE: 'medical_guideline',
  ERROR: 'error'
} as const;

interface BaseAnalyticsEvent {
  event_category: string;
  event_label?: string;
  value?: number;
}

interface PageViewEvent extends BaseAnalyticsEvent {
  page_title: string;
  page_location: string;
  custom_parameter?: string;
}

interface UserOnboardingEvent extends BaseAnalyticsEvent {
  user_role?: string;
  user_location?: string;
  user_district?: string;
  practice_area?: string;
  is_maori_pacific?: boolean;
}

interface GuidelineInteractionEvent extends BaseAnalyticsEvent {
  guideline_type?: string;
  recommendation_id?: string;
  recommendation_name?: string;
  antibiotic_selected?: string;
  condition_selected?: string;
}

interface FeedbackEvent extends BaseAnalyticsEvent {
  feedback_type: string;
  recommendation_id?: string;
  user_role?: string;
  has_email?: boolean;
}

interface AntibioticsFormEvent extends BaseAnalyticsEvent {
  practice_type: string;
  patient_type: string;
}

export class Analytics {
  private static isGtagAvailable(): boolean {
    return typeof window !== 'undefined' && typeof window.gtag === 'function';
  }

  static trackPageView(page: string, title?: string, customData?: Record<string, any>) {
    if (!this.isGtagAvailable()) return;

    const pageData: PageViewEvent = {
      event_category: EVENT_CATEGORIES.NAVIGATION,
      page_title: title || document.title,
      page_location: window.location.href,
      ...customData
    };

    window.gtag('event', 'page_view', pageData);
    console.log('Page View Tracked:', page, pageData);
  }

  static trackOnboardingComplete(userData: UserOnboardingEvent) {
    if (!this.isGtagAvailable()) return;

    const eventData = {
      event_label: 'onboarding_completed',
      ...userData,
      event_category: EVENT_CATEGORIES.FORM_INTERACTION
    };

    window.gtag('event', 'onboarding_complete', eventData);
    console.log('Onboarding Completed:', eventData);
  }

  static trackGuidelineInteraction(action: string, data: GuidelineInteractionEvent) {
    if (!this.isGtagAvailable()) return;

    const eventData = {
      event_label: action,
      ...data,
      event_category: EVENT_CATEGORIES.MEDICAL_GUIDELINE
    };

    window.gtag('event', `guideline_${action}`, eventData);
    console.log('Guideline Interaction:', action, eventData);
  }

  static trackFeedbackSubmission(data: FeedbackEvent) {
    if (!this.isGtagAvailable()) return;

    const eventData = {
      event_label: 'feedback_submitted',
      ...data,
      event_category: EVENT_CATEGORIES.FORM_INTERACTION
    };

    window.gtag('event', 'feedback_submission', eventData);
    console.log('Feedback Submitted:', eventData);
  }

  static trackAntibioticsFormSubmission(data: AntibioticsFormEvent) {
    if (!this.isGtagAvailable()) return;

    const eventData = {
      event_label: 'antibiotics_form_submitted',
      ...data,
      event_category: EVENT_CATEGORIES.FORM_INTERACTION
    };

    window.gtag('event', 'antibiotics_form_submission', eventData);
    console.log('Antibiotics Form Submitted:', eventData);
  }

  static trackButtonClick(buttonName: string, location: string, additionalData?: Record<string, any>) {
    if (!this.isGtagAvailable()) return;

    const eventData = {
      event_category: EVENT_CATEGORIES.USER_ENGAGEMENT,
      event_label: buttonName,
      button_location: location,
      ...additionalData
    };

    window.gtag('event', 'button_click', eventData);
    console.log('Button Click:', buttonName, eventData);
  }

  static trackFormInteraction(formName: string, action: string, fieldName?: string) {
    if (!this.isGtagAvailable()) return;

    const eventData = {
      event_category: EVENT_CATEGORIES.FORM_INTERACTION,
      event_label: `${formName}_${action}`,
      form_name: formName,
      action: action,
      field_name: fieldName
    };

    window.gtag('event', 'form_interaction', eventData);
    console.log('Form Interaction:', formName, action, eventData);
  }

  static trackContentEngagement(contentType: string, action: string, contentId?: string) {
    if (!this.isGtagAvailable()) return;

    const eventData = {
      event_category: EVENT_CATEGORIES.CONTENT_INTERACTION,
      event_label: `${contentType}_${action}`,
      content_type: contentType,
      content_id: contentId
    };

    window.gtag('event', 'content_engagement', eventData);
    console.log('Content Engagement:', contentType, action, eventData);
  }

  static trackScrollDepth(percentage: number, page: string) {
    if (!this.isGtagAvailable()) return;

    const eventData = {
      event_category: EVENT_CATEGORIES.USER_ENGAGEMENT,
      event_label: 'scroll_depth',
      scroll_percentage: percentage,
      page: page,
      value: percentage
    };

    window.gtag('event', 'scroll', eventData);
    console.log('Scroll Depth:', percentage, page);
  }

  static trackError(errorType: string, errorMessage: string, location?: string) {
    if (!this.isGtagAvailable()) return;

    const eventData = {
      event_category: EVENT_CATEGORIES.ERROR,
      event_label: errorType,
      error_message: errorMessage,
      error_location: location || window.location.pathname,
      fatal: false
    };

    window.gtag('event', 'exception', eventData);
    console.log('Error Tracked:', errorType, errorMessage);
  }

  static trackCustomEvent(eventName: string, parameters: Record<string, any>) {
    if (!this.isGtagAvailable()) return;

    window.gtag('event', eventName, parameters);
    console.log('Custom Event:', eventName, parameters);
  }

  static setUserProperties(properties: Record<string, any>) {
    if (!this.isGtagAvailable()) return;

    window.gtag('config', 'G-HWD4Z8G4DS', {
      user_properties: properties
    });
    console.log('User Properties Set:', properties);
  }

  static trackTiming(category: string, variable: string, value: number, label?: string) {
    if (!this.isGtagAvailable()) return;

    const eventData = {
      event_category: category,
      event_label: label || variable,
      value: Math.round(value),
      timing_category: category,
      timing_variable: variable
    };

    window.gtag('event', 'timing_complete', eventData);
    console.log('Timing Tracked:', category, variable, value);
  }
}

export class HealthcareAnalytics {
  static trackRecommendationView(recommendationId: string, recommendationName: string, antibiotic: string, condition?: string) {
    Analytics.trackGuidelineInteraction('recommendation_viewed', {
      event_category: EVENT_CATEGORIES.MEDICAL_GUIDELINE,
      recommendation_id: recommendationId,
      recommendation_name: recommendationName,
      antibiotic_selected: antibiotic,
      condition_selected: condition
    });
  }

  static trackDisclaimerAcceptance(userRole?: string) {
    Analytics.trackCustomEvent('disclaimer_accepted', {
      event_category: EVENT_CATEGORIES.USER_ENGAGEMENT,
      user_role: userRole,
      timestamp: new Date().toISOString()
    });
  }

  static trackGuidelineSearch(searchQuery?: string, filters?: Record<string, any>) {
    Analytics.trackCustomEvent('guideline_search', {
      event_category: EVENT_CATEGORIES.MEDICAL_GUIDELINE,
      search_query: searchQuery,
      filters_applied: JSON.stringify(filters),
      results_found: filters ? Object.keys(filters).length : 0
    });
  }

  static trackExpertConsultation(expertType: string, location: string) {
    Analytics.trackCustomEvent('expert_consultation_requested', {
      event_category: EVENT_CATEGORIES.MEDICAL_GUIDELINE,
      expert_type: expertType,
      user_location: location
    });
  }
}

export const useScrollDepthTracking = (pageName: string) => {
  if (typeof window === 'undefined') return;

  let maxScrollDepth = 0;
  const milestones = [25, 50, 75, 100];
  const trackedMilestones = new Set<number>();

  const handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const documentHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const scrollPercentage = Math.round((scrollTop / documentHeight) * 100);

    maxScrollDepth = Math.max(maxScrollDepth, scrollPercentage);

    milestones.forEach(milestone => {
      if (scrollPercentage >= milestone && !trackedMilestones.has(milestone)) {
        trackedMilestones.add(milestone);
        Analytics.trackScrollDepth(milestone, pageName);
      }
    });
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  
  return () => {
    window.removeEventListener('scroll', handleScroll);
    if (maxScrollDepth > 0) {
      Analytics.trackCustomEvent('page_scroll_complete', {
        event_category: EVENT_CATEGORIES.USER_ENGAGEMENT,
        max_scroll_depth: maxScrollDepth,
        page: pageName
      });
    }
  };
};

export const setupGlobalErrorTracking = () => {
  if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
      Analytics.trackError(
        'unhandled_promise_rejection',
        event.reason?.message || event.reason?.toString() || 'Unknown promise rejection',
        window.location.pathname
      );
    });

    window.addEventListener('error', (event) => {
      Analytics.trackError(
        'javascript_error',
        event.message || 'Unknown JavaScript error',
        `${event.filename}:${event.lineno}:${event.colno}`
      );
    });
  }
};

export default Analytics;
